import { useState, useRef, useEffect } from "react";
import api, { MAD, apiError } from "../lib/api";
import { useData } from "../hooks/useData";
import { useAuth } from "../context/AuthContext";
import { usePos } from "../context/PosContext";
import { PageHeader, ExportButtons, Loader, Empty, StatCard } from "../components/ui-kit";
import { Modal, Field } from "../components/Modal";
import { exportExcel, exportPDF } from "../lib/exporters";
import { Plus, Trash2, X, Receipt, Banknote, TrendingUp, ScanLine, Loader2, Pencil, FileEdit } from "lucide-react";
import { toast } from "sonner";

const DRAFT_KEY = "tm_sale_draft";

export default function Sales() {
  const { isAdmin, can, user } = useAuth();
  const { salesPosList, posList } = usePos();
  const { data, loading, refetch } = useData("/sales");
  const { data: products } = useData("/products");
  const [open, setOpen] = useState(false);
  const [editId, setEditId] = useState(null);
  const [cart, setCart] = useState([]);
  const [sel, setSel] = useState("");
  const [qty, setQty] = useState(1);
  const [mode, setMode] = useState("especes");
  const [saleDate, setSaleDate] = useState("");
  const [salePos, setSalePos] = useState("");
  const [note, setNote] = useState("");
  const [reference, setReference] = useState("");
  const [espMixte, setEspMixte] = useState("");
  const [scanned, setScanned] = useState(false);
  const [scanning, setScanning] = useState(false);
  const [draft, setDraft] = useState(() => { try { return JSON.parse(localStorage.getItem(DRAFT_KEY)); } catch { return null; } });
  const [draftPrompt, setDraftPrompt] = useState(false);
  const fileRef = useRef(null);

  const defaultPos = () => (isAdmin ? (salesPosList[0]?.id || "") : (user?.pos_id || ""));
  const posName = (id) => posList.find((p) => p.id === id)?.nom || user?.pos_nom || "—";
  const today = () => new Date().toISOString().slice(0, 10);

  const clearDraft = () => { localStorage.removeItem(DRAFT_KEY); setDraft(null); };

  // Sauvegarde automatique du brouillon (uniquement pour une nouvelle vente avec du contenu).
  useEffect(() => {
    if (!open || editId) return;
    if (cart.length === 0 && !note.trim()) return;
    const obj = { salePos, cart, mode, saleDate, note, savedAt: Date.now() };
    localStorage.setItem(DRAFT_KEY, JSON.stringify(obj));
    setDraft(obj);
  }, [open, editId, cart, salePos, mode, saleDate, note]); // eslint-disable-line

  const startFresh = () => {
    setEditId(null); setCart([]); setSel(products[0]?.id || ""); setQty(1);
    setMode("especes"); setSaleDate(today()); setSalePos(defaultPos()); setNote(""); setReference(""); setEspMixte(""); setScanned(false); setOpen(true);
  };
  const openNew = () => { if (draft && (draft.cart?.length || draft.note)) setDraftPrompt(true); else startFresh(); };

  const continueDraft = () => {
    setEditId(null);
    setCart(draft.cart || []); setSalePos(draft.salePos || defaultPos());
    setMode(draft.mode || "especes"); setSaleDate(draft.saleDate || today()); setNote(draft.note || "");
    setSel(products[0]?.id || ""); setQty(1); setScanned(false);
    setDraftPrompt(false); setOpen(true);
  };
  const discardDraftPrompt = () => { clearDraft(); setDraftPrompt(false); };
  const newFromPrompt = () => { clearDraft(); setDraftPrompt(false); startFresh(); };

  const openEdit = (s) => {
    setEditId(s.id);
    setCart((s.items || []).map((i) => ({ product_id: i.product_id, nom: i.nom, quantite: i.quantite, prix_unitaire: i.prix_unitaire, cout_unitaire: i.cout_unitaire || 0 })));
    setSalePos(s.pos_id || defaultPos()); setMode(s.mode_paiement || "especes");
    setSaleDate(s.date ? s.date.slice(0, 10) : today()); setNote(s.note || ""); setReference(s.reference || "");
    if (s.montant_especes != null && s.montant_carte != null && s.montant_especes > 0 && s.montant_carte > 0) { setMode("mixte"); setEspMixte(s.montant_especes); } else setEspMixte("");
    setSel(products[0]?.id || ""); setQty(1); setScanned(false); setOpen(true);
  };

  const addItem = () => {
    const p = products.find((x) => x.id === sel);
    if (!p || qty < 1) return;
    setCart([...cart, { product_id: p.id, nom: p.nom, quantite: Number(qty), prix_unitaire: p.prix_vente, cout_unitaire: p.cout_matiere }]);
    setQty(1);
  };

  const updateLine = (idx, patch) => setCart(cart.map((l, i) => (i === idx ? { ...l, ...patch } : l)));
  const changeLineProduct = (idx, pid) => {
    const p = products.find((x) => x.id === pid);
    if (!p) return updateLine(idx, { product_id: "", nom: "" });
    const cur = cart[idx];
    updateLine(idx, { product_id: p.id, nom: p.nom, cout_unitaire: p.cout_matiere, prix_unitaire: cur.prix_unitaire || p.prix_vente });
  };

  const onScanClick = () => fileRef.current?.click();
  const onFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setScanning(true);
    try {
      const b64 = await new Promise((resolve, reject) => {
        const r = new FileReader();
        r.onload = () => resolve(r.result);
        r.onerror = reject;
        r.readAsDataURL(file);
      });
      const { data: res } = await api.post("/sales/scan-ticket", { image_base64: b64 });
      const lines = (res.items || []).map((it) => {
        const p = products.find((x) => x.id === it.product_id);
        return {
          product_id: it.product_id || "",
          nom: p ? p.nom : it.nom_ticket || it.nom,
          nom_ticket: it.nom_ticket || "",
          quantite: it.quantite || 1,
          prix_unitaire: it.prix_unitaire || (p ? p.prix_vente : 0),
          cout_unitaire: p ? p.cout_matiere : 0,
        };
      });
      setEditId(null); setCart(lines); setSel(products[0]?.id || ""); setQty(1);
      setMode(res.mode_paiement || "especes");
      const validDate = res.date && /^\d{4}-\d{2}-\d{2}$/.test(res.date) ? res.date : today();
      setSaleDate(validDate); setNote(""); setScanned(true); setSalePos(defaultPos()); setOpen(true);
      if (lines.length === 0) toast.warning("Aucune ligne détectée — ajoutez les produits manuellement.");
      else toast.success(`${lines.length} ligne(s) détectée(s) — vérifiez avant d'enregistrer.`);
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail) || "Analyse du ticket impossible");
    } finally {
      setScanning(false);
    }
  };

  const cartTotal = cart.reduce((a, i) => a + i.quantite * i.prix_unitaire, 0);

  const save = async () => {
    if (cart.length === 0) return toast.error("Ajoutez au moins un produit");
    if (cart.some((l) => !l.product_id)) return toast.error("Associez chaque ligne à un produit existant");
    if (cart.some((l) => !l.quantite || l.quantite < 1)) return toast.error("Quantité invalide sur une ligne");
    if (!salePos) return toast.error("Sélectionnez le point de vente concerné");
    try {
      const items = cart.map((l) => ({ product_id: l.product_id, nom: l.nom, quantite: Number(l.quantite), prix_unitaire: Number(l.prix_unitaire), cout_unitaire: Number(l.cout_unitaire || 0) }));
      const tot = items.reduce((a, l) => a + l.quantite * l.prix_unitaire, 0);
      const split = mode === "mixte" ? { montant_especes: Number(espMixte) || 0, montant_carte: Math.round((tot - (Number(espMixte) || 0)) * 100) / 100 } : {};
      const payload = { items, mode_paiement: mode, note, reference, ...split, date: saleDate ? new Date(saleDate).toISOString() : "" };
      if (editId) {
        await api.put(`/sales/${editId}?pos_id=${salePos}`, payload);
        toast.success("Vente modifiée — calculs actualisés");
      } else {
        try { await api.post(`/sales?pos_id=${salePos}`, payload); }
        catch (e1) {
          const det = e1.response?.data?.detail;
          if (e1.response?.status !== 409 || det?.type !== "doublon") throw e1;
          if (!window.confirm(`${det.message}\n\nEnregistrer quand même cette vente ?`)) return;
          await api.post(`/sales?pos_id=${salePos}&force=true`, payload);
        }
        toast.success("Vente enregistrée");
        clearDraft();
      }
      setOpen(false); setEditId(null); refetch();
    } catch (e) { const det = e.response?.data?.detail; toast.error(det?.message || apiError(det)); }
  };

  const remove = async (s) => {
    if (!window.confirm("Supprimer cette vente ?")) return;
    await api.delete(`/sales/${s.id}`); toast.success("Supprimée"); refetch();
  };

  const fmt = (s) => new Date(s).toLocaleString("fr-FR", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" });

  const totalCA = data.reduce((a, s) => a + s.total, 0);
  const totalBenef = data.reduce((a, s) => a + s.benefice, 0);
  const showActions = can("ventes", "edit") || can("ventes", "delete");

  const cols = [
    { header: "Date", value: (r) => fmt(r.date) },
    { header: "Point de vente", value: (r) => posName(r.pos_id) },
    { header: "Articles", value: (r) => r.items.map((i) => `${i.quantite}× ${i.nom}`).join(", ") },
    { header: "Total (DH)", value: (r) => r.total },
    { header: "Bénéfice (DH)", value: (r) => r.benefice },
    { header: "Paiement", value: (r) => r.mode_paiement },
  ];

  return (
    <div>
      <PageHeader title="Ventes journalières" subtitle="Historique et enregistrement des ventes.">
        {can("ventes", "export") && <ExportButtons onExcel={() => exportExcel("ventes_tiramessi", cols, data)} onPDF={() => exportPDF("Ventes TIRAMESSI", cols, data)} />}
        {can("ventes", "create") && (
          <>
            <input ref={fileRef} type="file" accept="image/*" capture="environment" className="hidden" onChange={onFile} data-testid="ticket-file-input" />
            <button className="tm-btn-ghost tm-btn" onClick={onScanClick} disabled={scanning} data-testid="scan-ticket-btn">
              {scanning ? <Loader2 size={16} className="animate-spin" /> : <ScanLine size={16} />} {scanning ? "Analyse..." : "Scanner un ticket"}
            </button>
          </>
        )}
        {can("ventes", "create") && <button className="tm-btn" onClick={openNew} data-testid="add-sale-btn"><Plus size={16} /> Nouvelle vente</button>}
      </PageHeader>

      {/* Brouillon en cours */}
      {can("ventes", "create") && draft && (draft.cart?.length || draft.note) && !open && (
        <div className="tm-card p-3 mb-5 flex items-center justify-between gap-3 flex-wrap" style={{ background: "rgba(212,175,55,.08)", border: "1px solid var(--tm-gold)" }} data-testid="draft-banner">
          <span className="text-sm flex items-center gap-2"><FileEdit size={15} style={{ color: "var(--tm-gold)" }} />
            Brouillon de vente en cours ({draft.cart?.length || 0} article(s)) — {new Date(draft.savedAt).toLocaleString("fr-FR")}
          </span>
          <div className="flex gap-2">
            <button className="tm-btn tm-btn-sm text-xs py-1.5" onClick={continueDraft} data-testid="draft-continue">Continuer</button>
            <button className="tm-btn-ghost tm-btn text-xs py-1.5" onClick={clearDraft} data-testid="draft-delete">Supprimer</button>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
        <StatCard label="CA cumulé" value={MAD(totalCA)} icon={TrendingUp} testid="sales-ca" />
        <StatCard label="Bénéfice cumulé" value={MAD(totalBenef)} icon={Banknote} accent="#D4AF37" testid="sales-benefice" />
        <StatCard label="Nombre de ventes" value={data.length} icon={Receipt} testid="sales-count" />
      </div>

      {loading ? <Loader /> : data.length === 0 ? <Empty text="Aucune vente enregistrée." /> : (
        <div className="tm-card overflow-x-auto" data-testid="sales-table">
          <table className="tm-table">
            <thead>
              <tr><th>Date</th><th>Point de vente</th><th>Articles</th><th className="text-right">Total</th><th className="text-right">Bénéfice</th><th>Paiement</th>{showActions && <th></th>}</tr>
            </thead>
            <tbody>
              {data.map((s) => (
                <tr key={s.id} data-testid={`sale-row-${s.id}`}>
                  <td className="text-[var(--tm-muted)] whitespace-nowrap">{fmt(s.date)}</td>
                  <td className="whitespace-nowrap font-semibold" data-testid={`sale-pos-${s.id}`}>{posName(s.pos_id)}</td>
                  <td>{s.items.map((i) => `${i.quantite}× ${i.nom}`).join(", ")}</td>
                  <td className="text-right tabular font-semibold">{MAD(s.total)}</td>
                  <td className="text-right tabular" style={{ color: "#4CAF50" }}>{MAD(s.benefice)}</td>
                  <td><span className="tm-badge" style={{ background: "var(--tm-surface-2)", color: "var(--tm-muted)" }}>{s.mode_paiement}</span></td>
                  {showActions && (
                    <td className="text-right whitespace-nowrap">
                      {can("ventes", "edit") && <button onClick={() => openEdit(s)} className="text-[var(--tm-muted)] hover:text-[var(--tm-gold)] mr-3" data-testid={`edit-sale-${s.id}`} title="Modifier"><Pencil size={15} /></button>}
                      {can("ventes", "delete") && <button onClick={() => remove(s)} className="text-[var(--tm-muted)] hover:text-[#F44336]" data-testid={`delete-sale-${s.id}`} title="Supprimer"><Trash2 size={16} /></button>}
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Choix brouillon */}
      <Modal open={draftPrompt} onClose={() => setDraftPrompt(false)} title="Brouillon de vente détecté" testid="draft-prompt-modal">
        <p className="text-sm text-[var(--tm-muted)] mb-4">Un brouillon de vente non validé existe ({draft?.cart?.length || 0} article(s), enregistré le {draft ? new Date(draft.savedAt).toLocaleString("fr-FR") : ""}). Que souhaitez-vous faire ?</p>
        <div className="space-y-2">
          <button className="tm-btn w-full" onClick={continueDraft} data-testid="prompt-continue">Continuer le brouillon</button>
          <button className="tm-btn-ghost tm-btn w-full" onClick={newFromPrompt} data-testid="prompt-new">Créer une nouvelle vente</button>
          <button className="tm-btn-ghost tm-btn w-full" style={{ color: "#F44336" }} onClick={discardDraftPrompt} data-testid="prompt-discard">Supprimer le brouillon</button>
        </div>
      </Modal>

      <Modal open={open} onClose={() => { setOpen(false); setEditId(null); }} title={editId ? "Modifier la vente" : scanned ? "Vérifier la vente scannée" : "Nouvelle vente"} testid="sale-modal" wide>
        {scanned && !editId && (
          <div className="tm-card p-3 mb-4 text-sm" style={{ background: "rgba(212,175,55,.1)", border: "1px solid var(--tm-gold)" }} data-testid="scan-review-banner">
            <ScanLine size={14} className="inline mr-1" style={{ color: "var(--tm-gold)" }} />
            Ticket analysé automatiquement. Corrigez les produits, quantités et prix si nécessaire, puis validez. Rien n'est enregistré tant que vous ne validez pas.
          </div>
        )}
        <Field label="Point de vente concerné">
          {isAdmin ? (
            <select className="tm-input" value={salePos} onChange={(e) => setSalePos(e.target.value)} data-testid="sale-pos">
              {salesPosList.length === 0 && <option value="">Aucun point de vente actif</option>}
              {salesPosList.map((p) => <option key={p.id} value={p.id}>{p.nom}</option>)}
            </select>
          ) : (
            <input className="tm-input" value={user?.pos_nom || "—"} disabled data-testid="sale-pos" />
          )}
        </Field>
        <div className="flex gap-2 items-end mb-4">
          <div className="flex-1">
            <span className="block text-xs uppercase tracking-wider text-[var(--tm-muted)] mb-1.5">Ajouter un produit</span>
            <select className="tm-input" value={sel} onChange={(e) => setSel(e.target.value)} data-testid="sale-product">
              {products.map((p) => <option key={p.id} value={p.id}>{p.nom} — {MAD(p.prix_vente)}</option>)}
            </select>
          </div>
          <div className="w-20">
            <span className="block text-xs uppercase tracking-wider text-[var(--tm-muted)] mb-1.5">Qté</span>
            <input type="number" min="1" className="tm-input" value={qty} onChange={(e) => setQty(e.target.value)} data-testid="sale-qty" />
          </div>
          <button type="button" className="tm-btn-ghost tm-btn" onClick={addItem} data-testid="sale-add-item"><Plus size={16} /></button>
        </div>

        {cart.length > 0 && (
          <div className="tm-card p-3 mb-4" style={{ background: "var(--tm-bg)" }} data-testid="sale-cart">
            <div className="hidden sm:grid grid-cols-[1fr_5rem_6rem_5.5rem_auto] gap-2 text-[10px] uppercase tracking-wider text-[var(--tm-muted)] mb-2 px-1">
              <span>Produit</span><span className="text-center">Qté</span><span className="text-center">Prix unit.</span><span className="text-right">Total</span><span></span>
            </div>
            {cart.map((i, idx) => (
              <div key={idx} className="grid grid-cols-[1fr_5rem_6rem_5.5rem_auto] gap-2 items-center py-1.5" data-testid={`cart-line-${idx}`}>
                <select className="tm-input py-1.5 text-sm" value={i.product_id} onChange={(e) => changeLineProduct(idx, e.target.value)} data-testid={`cart-product-${idx}`} style={!i.product_id ? { borderColor: "#F44336" } : {}}>
                  <option value="">{i.nom_ticket ? `⚠ ${i.nom_ticket}` : "— Choisir —"}</option>
                  {products.map((p) => <option key={p.id} value={p.id}>{p.nom}</option>)}
                </select>
                <input type="number" min="1" className="tm-input py-1.5 text-sm text-center" value={i.quantite} onChange={(e) => updateLine(idx, { quantite: Number(e.target.value) })} data-testid={`cart-qty-${idx}`} />
                <input type="number" min="0" step="0.01" className="tm-input py-1.5 text-sm text-center" value={i.prix_unitaire} onChange={(e) => updateLine(idx, { prix_unitaire: Number(e.target.value) })} data-testid={`cart-price-${idx}`} />
                <span className="tabular text-sm text-right">{MAD(i.quantite * i.prix_unitaire)}</span>
                <button onClick={() => setCart(cart.filter((_, x) => x !== idx))} className="text-[var(--tm-muted)] hover:text-[#F44336]" data-testid={`cart-remove-${idx}`}><X size={15} /></button>
              </div>
            ))}
            <div className="flex justify-between pt-2 mt-1 font-semibold" style={{ borderTop: "1px solid var(--tm-border)" }}>
              <span>Total</span><span className="tabular" style={{ color: "var(--tm-gold)" }}>{MAD(cartTotal)}</span>
            </div>
          </div>
        )}

        <div className="grid grid-cols-2 gap-3">
          <Field label="Date de la vente">
            <input type="date" className="tm-input" value={saleDate} onChange={(e) => setSaleDate(e.target.value)} data-testid="sale-date" />
          </Field>
          <Field label="Mode de paiement">
            <select className="tm-input" value={mode} onChange={(e) => setMode(e.target.value)} data-testid="sale-mode">
              <option value="especes">Espèces</option><option value="carte">Carte</option><option value="virement">Virement</option><option value="mixte">Espèces + carte</option>
            </select>
          </Field>
        </div>
        {mode === "mixte" && (
          <div className="grid grid-cols-2 gap-3" data-testid="sale-split">
            <Field label="Dont espèces (DH)"><input type="number" step="0.01" min="0" className="tm-input" value={espMixte} onChange={(e) => setEspMixte(e.target.value)} data-testid="sale-montant-especes" /></Field>
            <Field label="Dont carte (DH)"><input className="tm-input" readOnly value={Math.round((cartTotal - (Number(espMixte) || 0)) * 100) / 100} data-testid="sale-montant-carte" /></Field>
          </div>
        )}
        <Field label="Référence (n° de clôture de caisse, facultatif)">
          <input className="tm-input" value={reference} onChange={(e) => setReference(e.target.value)} placeholder="Z-0123" data-testid="sale-reference" />
        </Field>
        <Field label="Commentaire (facultatif)">
          <input className="tm-input" value={note} onChange={(e) => setNote(e.target.value)} placeholder="Remarque sur la vente..." data-testid="sale-note" />
        </Field>
        <button className="tm-btn w-full mt-2" onClick={save} data-testid="sale-save">{editId ? "Enregistrer les modifications" : scanned ? "Valider et enregistrer la vente" : "Valider la vente"}</button>
      </Modal>
    </div>
  );
}
